import { api } from './client';

export interface ScraperState {
    searchKey: string;
    timestamp: number;
    lastChange: number;
    lastScrapedUrl?: string;
    apiSearchUrl?: string;
    publicSearchUrl?: string;
    deactivated?: boolean;
    listingIds: string[];
    recordType?: string;
    vendor?: boolean;
    adminNote?: string;
    lastError?: string;
    lastErrorType?: string;
    retryAfter?: string;
    errorCounter?: number;
    lastErrorTimestamp?: number;
    lastRecoverTimestamp?: number;
    tokenStatus?: 'fetching' | 'ready';
    lastTriggered?: number;
    isGroup?: boolean;
    parentGroupKey?: string;
    buckets?: ScraperState[];
    resultCount?: number;
    numOfNewEntries?: number;
    lastPositionOfStop?: number;
    schedule?: ScraperSchedule;
}

export interface UserData {
    userId: string;
    email?: string;
    tier: string;
    telegramId?: number;
    telegramDisplayName?: string;
    premiumExpiresAt?: number;
    emailVerified?: boolean;
    createdAt: number;
    updatedAt?: number;
    hasPassword?: boolean;
    devices?: Record<string, any>;
    subscriptionSummary?: { total: number; active: number };
}

export interface SystemHealth {
    maintenanceMode: boolean;
    maintenanceSeverity?: string | null;
    maintenanceText?: string | null;
    metadata: {
        zipCount: number;
    };
    subscriptions: {
        total: number;
    };
}

export const getScrapers = async (): Promise<ScraperState[]> => {
    const response = await api.get('/admin/scrapers');
    return response.data;
};

export const getUsers = async (): Promise<UserData[]> => {
    const response = await api.get('/admin/users');
    return response.data;
};

export const getSystemHealth = async (): Promise<SystemHealth> => {
    const response = await api.get('/admin/system/health');
    return response.data;
};

export const getUserSubscriptions = async (userId: string) => {
    const response = await api.get(`/admin/users/${userId}/subscriptions?includeDeleted=true`);
    return response.data;
};

export const rebuildSystem = async (type: 'metadata', force: boolean = false) => {
    const response = await api.post(`/admin/system/rebuild-metadata`, { force });
    return response.data;
};

export const acknowledgeError = async (searchKey: string) => {
    await api.post(`/admin/scrapers/${searchKey}/acknowledge-error`);
};

export interface BackoffStage {
    errorCount: number;
    delayMinutes: number;
}

export interface BackoffOverride {
    errorCount?: number;
    delayMinutes?: number;
}

export interface ScheduleProfile {
    id: string;
    name: string;
    multiplierWeekdayDay: number;
    multiplierWeekdayEvening: number;
    multiplierWeekdayNight: number;
    multiplierWeekendDay: number;
    multiplierWeekendEvening: number;
    multiplierWeekendNight: number;
    backoffCurve: BackoffStage[];
    deactivationThreshold: number;
}

export interface ScraperSchedule {
    scraperKey: string;
    profileId: string;
    regularIntervalMinutes: number;
    backoffOverride: BackoffOverride | null;
}

export interface ScraperBackoffState {
    searchKey: string;
    profileId: string | null;
    errorCount: number;
    effectiveBackoffDelayMs: number;
    backoffOverride: BackoffOverride | null;
}

export const getScheduleProfiles = async (): Promise<ScheduleProfile[]> => {
    const response = await api.get('/admin/schedules/profiles');
    return response.data;
};

export const createScheduleProfile = async (payload: Omit<ScheduleProfile, 'id'>): Promise<ScheduleProfile> => {
    const response = await api.post('/admin/schedules/profiles', payload);
    return response.data;
};

export const updateScheduleProfile = async (id: string, payload: Omit<ScheduleProfile, 'id'>): Promise<ScheduleProfile> => {
    const response = await api.put(`/admin/schedules/profiles/${encodeURIComponent(id)}`, payload);
    return response.data;
};

export const deleteScheduleProfile = async (id: string): Promise<void> => {
    await api.delete(`/admin/schedules/profiles/${encodeURIComponent(id)}`);
};

export const getScraperSchedule = async (searchKey: string): Promise<ScraperSchedule | null> => {
    try {
        const response = await api.get(`/admin/scrapers/${encodeURIComponent(searchKey)}/schedule`);
        return response.data;
    } catch (err: any) {
        if (err?.response?.status === 404) return null;
        throw err;
    }
};

export const setScraperSchedule = async (
    searchKey: string,
    payload: { profileId: string; regularIntervalMinutes: number }
): Promise<ScraperSchedule> => {
    const response = await api.put(`/admin/scrapers/${encodeURIComponent(searchKey)}/schedule`, payload);
    return response.data;
};

export const getScraperBackoffState = async (searchKey: string): Promise<ScraperBackoffState> => {
    const response = await api.get(`/admin/scrapers/${encodeURIComponent(searchKey)}/backoff`);
    return response.data;
};

export const setScraperBackoffOverride = async (
    searchKey: string,
    override: BackoffOverride | null
): Promise<ScraperBackoffState> => {
    const response = await api.put(`/admin/scrapers/${encodeURIComponent(searchKey)}/backoff-override`, override || {});
    return response.data;
};

export const toggleScraper = async (searchKey: string, activate: boolean) => {
    const urlPart = activate ? 'activate' : 'deactivate';
    await api.post(`/admin/scrapers/${encodeURIComponent(searchKey)}/${urlPart}`);
};

export const updateScraperNote = async (searchKey: string, note: string) => {
    await api.put(`/admin/scrapers/${encodeURIComponent(searchKey)}/note`, { note });
};

export const updateUserTier = async (userId: string, tier: string) => {
    await api.put(`/admin/users/${userId}/tier`, { tier });
};

export const updateUserTelegram = async (userId: string, telegramId: string | number) => {
    await api.put(`/admin/users/${userId}/telegram`, { telegramId });
};

export const updateUserExpiry = async (userId: string, expiry: number) => {
    await api.put(`/admin/users/${userId}/expiry`, { expiry });
};

export const resetUserPassword = async (userId: string) => {
    const newPassword = Math.random().toString(36).slice(-10);
    await api.put(`/admin/users/${userId}/password`, { password: newPassword });
    return newPassword;
};

export const sendPushTest = async (userId: string) => {
    await api.post(`/admin/users/${userId}/push-test`);
};

export const sendTestEmail = async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/test-email`);
    return response.data;
};

export const sendTestTelegram = async (userId: string) => {
    const response = await api.post(`/admin/users/${userId}/test-telegram`);
    return response.data;
};

export const updateUserEmail = async (userId: string, email: string | null) => {
    await api.put(`/admin/users/${userId}/email`, { email });
};

export const deleteUserAccount = async (userId: string) => {
    await api.delete(`/admin/users/${userId}`);
};

export const updateSubscription = async (subscriptionId: string, data: any) => {
    await api.put(`/admin/subscriptions/${subscriptionId}`, data);
};

export const deleteSubscription = async (subscriptionId: string) => {
    await api.delete(`/admin/subscriptions/${subscriptionId}`);
};

export const setMaintenanceMode = async (data: {
    active: boolean;
    severity?: string;
    text?: string;
}) => {
    await api.post('/admin/system/set-maintenance', data);
};

export const sendGlobalNotification = async (data: {
    message: string;
    channels: ('telegram' | 'push' | 'email')[];
    type: 'maintenance' | 'force-reload';
    title?: string;
}) => {
    await api.post('/admin/system/send-notification', data);
};

export interface StaleRecord {
    searchKey: string;
    lastChange: number;
    isGroup?: boolean;
    parentGroupKey?: string;
    reason: 'orphaned-bucket' | 'stale-group' | 'stale-bucket';
    listingCount: number;
}

export interface StaleResponse {
    staleRecords: StaleRecord[];
    totalHeadRecords: number;
    staleCount: number;
}

export const getStaleScrapers = async (): Promise<StaleResponse> => {
    const response = await api.get('/admin/scrapers/stale');
    return response.data;
};

export const deleteStaleScrapers = async (searchKeys: string[]): Promise<{ deleted: string[]; failed: string[] }> => {
    const response = await api.post('/admin/scrapers/stale/delete', { searchKeys });
    return response.data;
};

export const impersonateUser = async (userId: string): Promise<{ token: string; userId: string; email?: string; tier: string }> => {
    const response = await api.post(`/admin/users/${userId}/impersonate`);
    return response.data;
};

export const resetEmailVerification = async (userId: string): Promise<void> => {
    await api.post(`/admin/users/${userId}/reset-email-verification`);
};

// Schedule Control API
export interface ScheduleInfo {
    name: string;
    state: 'ENABLED' | 'DISABLED';
    scheduleExpression: string;
    timezone?: string;
    description?: string;
}

export interface ScheduleResponse {
    schedules: ScheduleInfo[];
    environment: string;
}

export const getScraperSchedules = async (): Promise<ScheduleResponse> => {
    const response = await api.get('/admin/scrapers/schedule');
    return response.data;
};

export const toggleScraperSchedules = async (enabled: boolean): Promise<void> => {
    await api.post('/admin/scrapers/schedule/toggle', { enabled });
};

export const triggerScraperRun = async (scraperFilter?: string[], testMode?: boolean): Promise<{ message: string; scraperFilter?: string[]; testMode?: boolean }> => {
    const body: Record<string, unknown> = {};
    if (scraperFilter && scraperFilter.length > 0) body.scraperFilter = scraperFilter;
    if (testMode) body.testMode = true;
    const response = await api.post('/admin/scrapers/trigger', Object.keys(body).length > 0 ? body : undefined);
    return response.data;
};
